import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, addDoc, serverTimestamp, where, limit } from 'firebase/firestore';
import { db, auth } from '../firebase';
import toast from 'react-hot-toast';

export interface Notification {
  id: string;
  message: string;
  title?: string;
  time: string;
  read: boolean;
  type: 'success' | 'info' | 'warning' | 'error' | 'payment' | 'kyc' | 'system' | 'promo';
  action?: () => void;
  actionLabel?: string;
  createdAt?: any;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
  removeNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  // Real-time listener for user notifications from Firestore
  useEffect(() => {
    // Listen to auth state changes to properly initialize
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (!user) {
        setNotifications([]);
        setLoading(false);
        return;
      }

      const notificationsRef = collection(db, 'users', user.uid, 'notifications');
      const q = query(notificationsRef, orderBy('createdAt', 'desc'), limit(50));

      const unsubscribeSnapshot = onSnapshot(q, (snapshot) => {
        const notifs: Notification[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          notifs.push({
            id: doc.id,
            message: data.message || '',
            title: data.title || '',
            read: data.read || false,
            type: data.type || 'info',
            time: formatTime(data.createdAt),
            createdAt: data.createdAt,
            actionLabel: data.actionLabel,
          });
        });
        setNotifications(notifs);
        setLoading(false);
      }, (error) => {
        console.error('❌ Notification listener error:', error);
        setLoading(false);
      });

      return () => {
        unsubscribeSnapshot();
      };
    });

    return () => unsubscribeAuth();
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  // Format time helper
  const formatTime = (timestamp: any) => {
    if (!timestamp) return 'Just now';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return 'Recently';
    }
  };

  const addNotification = useCallback(async (notification: Omit<Notification, 'id' | 'read'>) => {
    const user = auth.currentUser;
    if (!user) {
      console.warn('⚠️ Cannot add notification: User not authenticated');
      return;
    }

    try {
      // Add to Firestore (will auto-update via listener)
      const docRef = await addDoc(collection(db, 'users', user.uid, 'notifications'), {
        message: notification.message,
        title: notification.title || '',
        type: notification.type || 'info',
        read: false,
        createdAt: serverTimestamp(),
        actionLabel: notification.actionLabel || ''
      });

      // Show toast for immediate feedback
      const icon = getNotificationIcon(notification.type);
      if (notification.type === 'success') {
        toast.success(`${icon} ${notification.message}`);
      } else if (notification.type === 'error') {
        toast.error(`${icon} ${notification.message}`);
      } else if (notification.type === 'warning') {
        toast(`${icon} ${notification.message}`, { icon: '⚠️' });
      } else {
        toast(`${icon} ${notification.message}`);
      }
    } catch (error) {
      console.error('❌ Failed to add notification:', error);
    }
  }, []);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'payment': return '💰';
      case 'kyc': return '📄';
      case 'promo': return '🎁';
      case 'system': return '🔔';
      default: return 'ℹ️';
    }
  };

  const markAsRead = useCallback(async (id: string) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      await updateDoc(doc(db, 'users', user.uid, 'notifications', id), {
        read: true
      });
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const unreadNotifs = notifications.filter(n => !n.read);
      await Promise.all(
        unreadNotifs.map(notif =>
          updateDoc(doc(db, 'users', user.uid, 'notifications', notif.id), {
            read: true
          })
        )
      );
      toast.success('✅ All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error('Failed to update notifications');
    }
  }, [notifications]);

  const clearNotifications = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      await Promise.all(
        notifications.map(notif =>
          updateDoc(doc(db, 'users', user.uid, 'notifications', notif.id), {
            read: true
          })
        )
      );
      toast.success('✅ All notifications cleared');
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  }, [notifications]);

  const removeNotification = useCallback(async (id: string) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      await updateDoc(doc(db, 'users', user.uid, 'notifications', id), {
        read: true
      });
    } catch (error) {
      console.error('Failed to remove notification:', error);
    }
  }, []);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    removeNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
